import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { localAnalytics } from '../utils/analytics';
import { logErrorsWithMessage } from '../utils/errors';
import { usePurchases } from '../contexts/PurchasesContext';
import { fbTracking } from '../utils/fbTracking';
import { CloseButton, BlueButton, FontText } from './common';

interface DiscountPaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess: (customerInfo: any, productId: string) => void;
  bookTitle: string;
  chapterNumber: number;
}

export const DiscountPaywallModal: React.FC<DiscountPaywallModalProps> = ({
  isOpen,
  onClose,
  onPurchaseSuccess,
  bookTitle,
  chapterNumber,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { purchaseSubscriptionGuest } = usePurchases();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes in seconds
  const [countdownInterval, setCountdownInterval] =
    useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      startCountdown();
    } else {
      stopCountdown();
    }

    return () => {
      stopCountdown();
    };
  }, [isOpen]);

  const startCountdown = () => {
    setTimeLeft(5 * 60); // Reset to 5 minutes

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setCountdownInterval(interval);
  };

  const stopCountdown = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      setCountdownInterval(null);
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePurchase = async () => {
    setIsLoading(true);
    setError(null);

    try {
      localAnalytics().logEvent('UnauthChapterPurchaseInitiated', {
        screen: 'DiscountPaywall',
        action: 'PurchaseInitiated',
        productId: 'subscriptionweeklydiscount',
        price: '4.99',
        discount: true,
        bookTitle,
        chapterNumber,
      });

      fbTracking.trackInitiateCheckout(bookTitle, '4.99', true);

      const result = await purchaseSubscriptionGuest(
        'subscriptionweeklydiscount'
      );

      if (result.error) {
        throw new Error(result.error);
      }

      if (!result.success) {
        return;
      }

      localAnalytics().logEvent('UnauthChapterPurchaseSuccess', {
        screen: 'DiscountPaywall',
        action: 'PurchaseSuccess',
        productId: 'subscriptionweeklydiscount',
        price: '4.99',
        discount: true,
        bookTitle,
        chapterNumber,
      });

      fbTracking.trackPurchase(
        bookTitle,
        'subscriptionweeklydiscount',
        '4.99',
        true
      );

      onPurchaseSuccess(result.customerInfo, 'subscriptionweeklydiscount');
    } catch (error: any) {
      console.error('Discount purchase failed:', error);
      setError(error.message || 'Purchase failed. Please try again.');
      logErrorsWithMessage(error, 'Discount purchase failed');
      localAnalytics().logEvent('DiscountPurchaseError', {
        screen: 'DiscountPaywall',
        action: 'PurchaseError',
        error: error instanceof Error ? error.message : 'Unknown error',
        productId: 'subscriptionweeklydiscount',
        bookTitle,
        chapterNumber,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseClick = () => {
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const styles: Record<string, React.CSSProperties> = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 20,
    },
    modal: {
      position: 'relative',
      backgroundColor: theme.colors.white,
      borderRadius: 24,
      maxWidth: 335,
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
    },

    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 26,
      padding: '0px 24px 24px',
    },
    countdownContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: 153,
      alignItems: 'center',
      gap: 2,
      padding: '6px 4px',
      backgroundColor: '#e8342d',
      borderRadius: '0px 0px 12px 12px',
    },
    errorContainer: {
      backgroundColor: '#f8d7da',
      border: '1px solid #f5c6cb',
      borderRadius: 8,
      padding: 12,
      width: '100%',
    },
    errorText: {
      textAlign: 'center',
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 24,
      width: '100%',
    },
    discountImage: {
      width: 203,
      height: 118,
      objectFit: 'contain',
    },
    textContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 12,
      width: '100%',
      marginTop: -20,
    },
    subtitle: {
      textAlign: 'center',
      width: '100%',
    },
    title: {
      letterSpacing: -0.56,
      lineHeight: 1.1,
      textAlign: 'center',
      width: '100%',
      margin: 0,
    },
    priceContainer: {
      width: '100%',
      textAlign: 'center',
    },

    footer: {
      width: '100%',
    },
    footerLinks: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    footerLink: {
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    },
    footerDivider: {
      color: theme.colors.black,
    },
    loader: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 24,
      zIndex: 2,
    },
    spinner: {
      width: 40,
      height: 40,
      border: '4px solid #e3d3d6',
      borderTop: '4px solid #1e18b4',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: 16,
    },
    loaderText: {
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.overlay} onClick={handleCloseClick}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <CloseButton
          onClose={handleCloseClick}
          analyticsEvent="DiscountPaywallClosed"
          style={{ position: 'absolute', top: 12, right: 12 }}
          themeMode={'light'}
        />

        <div style={styles.content}>
          <div style={styles.countdownContainer}>
            <FontText
              normal
              style={{
                color: theme.colors.white,
                whiteSpace: 'nowrap',
                textAlign: 'center',
              }}
            >
              {t('expires_in')} {formatTime(timeLeft)}
            </FontText>
          </div>

          {error && (
            <div style={styles.errorContainer}>
              <div style={styles.errorText}>
                <FontText small style={{ color: theme.colors.text }}>
                  {error}
                </FontText>
              </div>
            </div>
          )}

          <div style={styles.header}>
            <img
              src="/img/books/40_percent_off.png"
              alt="40% off"
              style={styles.discountImage}
            />
            <div style={styles.textContent}>
              <div style={styles.subtitle}>
                <FontText small style={{ color: theme.colors.accent }}>
                  {t('first_week_discount')}
                </FontText>
              </div>
              <div style={styles.title}>
                <FontText h2>{t('discount_40_off')}</FontText>
              </div>
            </div>
          </div>

          <div style={styles.priceContainer}>
            <FontText normal style={{ lineHeight: 1.5 }}>
              {t('price_first_week')}
            </FontText>
          </div>

          <BlueButton
            onClick={handlePurchase}
            disabled={isLoading}
            loading={isLoading}
            style={{ width: '100%' }}
          >
            {isLoading ? t('processing') : t('claim_the_offer')}
          </BlueButton>

          <div style={styles.footer}>
            <div style={styles.footerLinks}>
              <a href="/terms" target="_blank" style={styles.footerLink}>
                <FontText small style={{ color: theme.colors.black }}>
                  {t('terms_conditions')}
                </FontText>
              </a>
              <span style={styles.footerDivider}>
                <FontText small>•</FontText>
              </span>
              <a href="/privacy" target="_blank" style={styles.footerLink}>
                <FontText small style={{ color: theme.colors.black }}>
                  {t('privacy_policy')}
                </FontText>
              </a>
            </div>
          </div>
        </div>

        {isLoading && (
          <div style={styles.loader}>
            <div style={styles.spinner} />
            <div style={styles.loaderText}>
              <FontText normal>{t('processing_purchase')}</FontText>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
